import React, { FC } from 'react';
import { Tooltip, makeStyles } from '@material-ui/core';
import type { Theme } from 'src/theme';
import { useTimezone } from 'src/features/timezone/hooks/useTimezone';
import { ZoneOption } from 'src/features/timezone/types/timezone.types';
import { InstantFormat, formatInstant, zoneAbbreviation } from 'src/features/timezone/utils/datetime';

const useStyles = makeStyles((theme: Theme) => ({
  heading: {
    fontWeight: theme.typography.fontWeightMedium as number,
    paddingBottom: theme.spacing(0.5)
  },
  table: {
    borderSpacing: 0
  },
  label: {
    padding: theme.spacing(0.25, 1.5, 0.25, 0),
    whiteSpace: 'nowrap'
  },
  time: {
    padding: theme.spacing(0.25, 1.5, 0.25, 0),
    whiteSpace: 'nowrap'
  },
  date: {
    padding: theme.spacing(0.25, 0),
    whiteSpace: 'nowrap'
  }
}));

interface DateTimeProps {
  value: string | null | undefined;
  format?: InstantFormat;
  zone?: string;
}

const rowLabel = (option: ZoneOption, value: string): string =>
  option.choice === 'utc' ? 'UTC' : `${zoneAbbreviation(option.zoneId, value)} · ${option.role}`;

const DateTime: FC<DateTimeProps> = ({ value, format = 'dateTime', zone }) => {
  const classes = useStyles();
  const { zone: ambientZone, options } = useTimezone();
  if (!value) return null;
  const displayZone = zone || ambientZone;
  const rows = options.filter(
    (option, index) => options.findIndex((other) => other.zoneId === option.zoneId) === index
  );
  return (
    <Tooltip
      title={
        <>
          <div className={classes.heading}>Time conversion</div>
          <table className={classes.table}>
            <tbody>
              {rows.map((option) => (
                <tr key={option.choice}>
                  <td className={classes.label}>{rowLabel(option, value)}</td>
                  <td className={classes.time}>{formatInstant(value, option.zoneId, 'time')}</td>
                  <td className={classes.date}>{formatInstant(value, option.zoneId, 'monthDay')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      }
    >
      <span>{formatInstant(value, displayZone, format)}</span>
    </Tooltip>
  );
};

export default DateTime;
